// Gráfico temporal: barras apiladas por clase (promedio por foto) + actividad + % casco.
// Arrastrar = acercar el período · doble clic = volver · clic en una barra = ver esas fotos abajo.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ComposedChart, Bar, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { ALL_CLASSES, classInfo, bucketLabel, fmtHM, photoUrl } from './iaData';
import { T } from './ui';
import { WX_ICONS, SkyDefs, SkyBand, WxIcon, SKY_CSS, skyCss } from './clima/Sky';

let chartSeq = 0;

function Thumb({ photo }) {
  const [u, setU] = useState(null);
  useEffect(() => { let a = true; photoUrl(photo).then((x) => a && setU(x)).catch(() => {}); return () => { a = false; }; }, [photo]);
  return <div style={{ width: 240, aspectRatio: `${photo.w} / ${photo.h}`, borderRadius: 8, background: '#f3f4f6', backgroundImage: u ? `url("${u}")` : 'none', backgroundSize: 'cover', marginBottom: 8 }} />;
}

function Tip({ active, payload, rows, gran, series, photoAt }) {
  if (!active || !payload || !payload.length) return null;
  const row = rows[payload[0].payload.idx];
  if (!row) return null;
  const head = { foto: 'Foto', hora: 'Hora', dia: 'Día', semana: 'Semana del', mes: 'Mes' }[gran];
  const cls = ALL_CLASSES.filter((c) => row[c] > 0).sort((a, b) => row[b] - row[a]);
  return (
    <div style={{ background: '#fff', border: `1px solid ${T.line}`, borderRadius: 12, padding: 12, color: T.text, maxWidth: 300, boxShadow: '0 10px 25px rgba(0,0,0,.12)' }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        {head} → {bucketLabel(row.start, gran)}
        {gran !== 'foto' && <span style={{ color: T.muted, fontWeight: 500 }}> · {row.n ? `${row.n} fotos` : 'sin fotos analizadas'}</span>}
      </div>
      {gran === 'foto' && photoAt && photoAt.get(row.start) && <Thumb photo={photoAt.get(row.start)} />}
      {series.clima && row.wx && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: skyCss(row.wx.kind), border: `1px solid ${T.line}`, borderRadius: 10, padding: '6px 10px 6px 6px', marginBottom: 8, fontSize: 12 }}>
          <img src={WX_ICONS[row.wx.kind]} alt="" width={44} height={44} style={{ filter: 'drop-shadow(0 2px 3px rgba(15,23,42,.2))' }} />
          <span style={{ lineHeight: 1.35 }}><b style={{ fontSize: 13 }}>{row.wx.label}</b><br />{row.wx.detail}</span>
        </div>
      )}
      {!row.n && series.clima && !row.wx && (row.temp != null || row.lluvia > 0) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {row.temp != null && <span style={pill('#e11d48')}>{row.temp}°C</span>}
          <span style={pill('#0284c7')}>{row.lluvia > 0 ? `Lluvia ${row.lluvia} mm` : 'Sin lluvia'}</span>
        </div>
      )}
      {!row.n && <div style={{ color: T.muted, fontSize: 12 }}>No hay fotos analizadas por la IA en este intervalo.</div>}
      {!!row.n && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {series.act && <span style={pill(T.green)}>Actividad {row.actividad}</span>}
        {series.casco && <span style={pill(T.yellow)}>Casco {row.casco == null ? '—' : row.casco + '%'}</span>}
        {series.clima && !row.wx && row.temp != null && <span style={pill('#e11d48')}>{row.temp}°C</span>}
        {series.clima && !row.wx && row.lluvia != null && <span style={pill('#0284c7')}>{row.lluvia > 0 ? `Lluvia ${row.lluvia} mm` : 'Sin lluvia'}</span>}
        <span style={pill('#64748b')}>Pico {row.pico} pers.</span>
      </div>}
      {!!row.n && <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {cls.length ? cls.map((c) => (
          <span key={c} style={pill(classInfo(c).color)}>{classInfo(c).label} {gran === 'foto' ? row[c] : row[c].toFixed(2)}</span>
        )) : <span style={{ color: T.muted, fontSize: 12 }}>Sin detecciones</span>}
      </div>}
      {!!row.n && <div style={{ color: T.muted, fontSize: 11, marginTop: 8 }}>
        {gran === 'foto' ? `Foto de las ${fmtHM(row.start)}. ` : 'Promedio por foto. '}Clic para ver estas fotos abajo.
      </div>}
    </div>
  );
}
const pill = (c) => ({ background: c + '14', color: c, border: `1px solid ${c}55`, borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 700 });

const dow = (ms) => (Math.floor((ms - 3 * 3600e3) / 864e5) + 4) % 7; // 0 = domingo, 6 = sábado

export default function TimeChart({ rows, gran, cfg, series, chartType, onZoom, onReset, onBucket, photoAt }) {
  const [sel, setSel] = useState(null); // {a, b} índices mientras se arrastra
  const uid = useMemo(() => `iadsky${(chartSeq += 1)}`, []);
  const boxRef = useRef(null);
  const [boxW, setBoxW] = useState(800);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((e) => setBoxW(e[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const data = useMemo(() => rows.map((r, idx) => ({ ...r, idx, _z: 0 })), [rows]);
  const classes = useMemo(() => ALL_CLASSES.filter((c) => cfg.enabled.includes(c) && rows.some((r) => r[c] > 0)), [rows, cfg.enabled]);

  const idxOf = (st) => {
    const v = st && (st.activeLabel ?? st.activeTooltipIndex ?? st.activeIndex);
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const up = () => {
    if (!sel) return;
    const a = Math.min(sel.a, sel.b ?? sel.a), b = Math.max(sel.a, sel.b ?? sel.a);
    setSel(null);
    if (a === b) { rows[a] && onBucket(rows[a]); return; }
    onZoom(rows[a].start, rows[b].end > rows[b].start ? rows[b].end : rows[b].start + 1);
  };

  // Franjas grises para sábados y domingos (agrupando barras seguidas)
  const weekends = useMemo(() => {
    if (!series.finde || !['foto', 'hora', 'dia'].includes(gran)) return [];
    const out = [];
    rows.forEach((r, i) => {
      const d = dow(r.start);
      if (d !== 0 && d !== 6) return;
      const last = out[out.length - 1];
      if (last && last.b === i - 1) last.b = i; else out.push({ a: i, b: i });
    });
    return out;
  }, [rows, gran, series.finde]);
  const maxRain = Math.max(1, ...rows.map((r) => r.lluvia || 0));
  // Sin ninguna foto analizada el eje de las detecciones no tiene series ni dominio y recharts no dibuja las
  // franjas del clima (que cuelgan de ese eje): se le engancha una línea invisible en 0 y un dominio fijo.
  const hasDet = rows.some((r) => r.n > 0);

  // Clima: fondo de cada columna según el tiempo (agrupando columnas seguidas iguales) + ícono arriba.
  // Si las columnas son angostas (celular, muchas barras) se muestra un ícono cada tanto, pero lluvia/tormenta siempre.
  const wx = useMemo(() => {
    if (!series.clima || !rows.some((r) => r.wx)) return null;
    const bands = [];
    rows.forEach((r, i) => {
      if (!r.wx) return;
      const last = bands[bands.length - 1];
      const same = last && last.b === i - 1 && last.kind === r.wx.kind && last.level === r.wx.level && last.night === r.wx.night;
      if (same) last.b = i; else bands.push({ a: i, b: i, kind: r.wx.kind, level: r.wx.level, night: r.wx.night });
    });
    // Tope de costo: con muchas franjas (90 días, etc.) el cielo queda quieto
    const animate = bands.filter((b) => ['chaparron', 'lluvia', 'tormenta', 'nieve', 'niebla'].includes(b.kind)).length <= 30;
    const plotW = Math.max(120, boxW - 52 - (series.casco ? 36 : 0));
    const colW = plotW / rows.length;
    // En vista por foto: un ícono por hora (en la primera foto de esa hora)
    const cand = [];
    rows.forEach((r, i) => { if (r.wx && (gran !== 'foto' || !cand.length || cand[cand.length - 1].wx.hour !== r.wx.hour)) cand.push({ i, wx: r.wx }); });
    const size = colW >= 30 || gran === 'foto' ? 26 : colW >= 20 ? 22 : 20;
    const minGap = size + 3; // px libres entre íconos para que no se pisen
    const step = Math.max(1, Math.ceil(minGap / Math.max(1, plotW / Math.max(1, cand.length))));
    const shown = [];
    const free = (c) => shown.every((o) => Math.abs(o.i - c.i) * colW >= minGap);
    cand.filter((c) => c.wx.rank >= 3).sort((a, b) => b.wx.rank - a.wx.rank).forEach((c) => free(c) && shown.push(c));
    cand.forEach((c, j) => { if (j % step === 0 && c.wx.rank < 3 && free(c)) shown.push(c); });
    return { bands, icons: shown, size, animate };
  }, [rows, series.clima, series.casco, boxW, gran]);

  const tick = (i) => (rows[i] ? (gran === 'foto' || gran === 'hora' ? fmtHM(rows[i].start) : bucketLabel(rows[i].start, gran)) : '');
  const stackType = chartType === 'lineas' ? null : 'det';

  return (
    <div ref={boxRef} className="iad-chart" onDoubleClick={onReset} style={{ width: '100%', height: 340, userSelect: 'none' }} title="Arrastrá para acercar · doble clic para volver">
      {wx && <style>{SKY_CSS}</style>}
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          accessibilityLayer={false}
          margin={{ top: wx ? 36 : 10, right: 8, left: -8, bottom: 0 }}
          onMouseDown={(st) => { const i = idxOf(st); if (i != null) setSel({ a: i, b: i }); }}
          onMouseMove={(st) => { if (sel) { const i = idxOf(st); if (i != null && i !== sel.b) setSel({ ...sel, b: i }); } }}
          onMouseUp={up}
          onMouseLeave={() => setSel(null)}
        >
          <defs>
            <linearGradient id="gAct" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={T.green} stopOpacity={0.3} />
              <stop offset="100%" stopColor={T.green} stopOpacity={0.02} />
            </linearGradient>
            {wx && <SkyDefs uid={uid} />}
          </defs>
          {weekends.map((w) => (
            <ReferenceArea key={'fs' + w.a} yAxisId="l" x1={w.a} x2={w.b} fill="#94a3b8" fillOpacity={series.clima ? 0.08 : 0.14} stroke="none" ifOverflow="extendDomain"
              label={w.b > w.a && gran === 'dia' && rows.length <= 31 ? { value: 'Fin de semana', position: 'insideTop', fill: '#94a3b8', fontSize: 10 } : undefined} />
          ))}
          {wx && wx.bands.map((b) => (
            <ReferenceArea key={'wx' + b.a} yAxisId="l" x1={b.a} x2={b.b} ifOverflow="extendDomain" zIndex={-50}
              shape={(p) => <SkyBand {...p} uid={uid} band={b} animate={wx.animate} />} />
          ))}
          <CartesianGrid stroke={T.line} strokeDasharray="0" vertical={false} />
          <XAxis dataKey="idx" type="category" tickFormatter={tick} tick={{ fill: T.muted, fontSize: 11 }} stroke={T.line} minTickGap={24} />
          <YAxis yAxisId="l" tick={{ fill: T.muted, fontSize: 11 }} stroke={T.line} width={44} domain={hasDet ? undefined : [0, 1]}
            label={{ value: 'Promedio por foto', angle: -90, position: 'insideLeft', fill: T.muted, fontSize: 11, dx: 14, dy: 50 }} />
          <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fill: T.muted, fontSize: 11 }} stroke={T.line} width={36} hide={!series.casco} unit="%" />
          <YAxis yAxisId="mm" orientation="right" domain={[0, maxRain * 3]} hide width={0} />
          <YAxis yAxisId="temp" orientation="right" domain={["dataMin - 4", "dataMax + 4"]} hide width={0} />
          <Tooltip content={<Tip rows={rows} gran={gran} series={series} photoAt={photoAt} />} cursor={{ fill: 'rgba(37,99,235,.06)' }} isAnimationActive={false} />

          {series.det && classes.map((c) => {
            const col = classInfo(c).color;
            if (chartType === 'areas') return <Area key={c} yAxisId="l" dataKey={c} stackId="det" stroke={col} fill={col} fillOpacity={0.55} isAnimationActive={false} type="monotone" />;
            if (chartType === 'lineas') return <Line key={c} yAxisId="l" dataKey={c} stroke={col} dot={false} strokeWidth={2} isAnimationActive={false} type="monotone" />;
            return <Bar key={c} yAxisId="l" dataKey={c} stackId={stackType} fill={col} isAnimationActive={false} maxBarSize={36} />;
          })}
          {!hasDet && <Line yAxisId="l" dataKey="_z" stroke="none" dot={false} activeDot={false} legendType="none" isAnimationActive={false} />}
          {series.act && <Area yAxisId="l" dataKey="actividad" type="monotone" stroke={T.green} strokeWidth={2} fill="url(#gAct)" isAnimationActive={false} dot={false} />}
          {series.clima && <Area yAxisId="mm" dataKey="lluvia" type="step" stroke="#0284c7" strokeOpacity={wx ? 0.5 : 1} strokeWidth={1} fill="#38bdf8" fillOpacity={wx ? 0.1 : 0.35} isAnimationActive={false} dot={false} />}
          {series.clima && <Line yAxisId="temp" dataKey="temp" type="monotone" stroke="#e11d48" strokeOpacity={wx ? 0.5 : 1} strokeWidth={wx ? 1.2 : 1.5} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} />}
          {series.casco && <Line yAxisId="r" dataKey="casco" type="monotone" stroke={T.yellow} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />}

          {wx && wx.icons.map((c) => (
            <ReferenceArea key={'wi' + c.i} yAxisId="l" x1={c.i} x2={c.i} fill="none" fillOpacity={0} stroke="none" ifOverflow="extendDomain" label={<WxIcon wx={c.wx} size={wx.size} />} />
          ))}

          {sel && sel.b != null && sel.a !== sel.b && (
            <ReferenceArea yAxisId="l" x1={Math.min(sel.a, sel.b)} x2={Math.max(sel.a, sel.b)} fill={T.blue} fillOpacity={0.18} stroke={T.blue} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
